/**
 * Assessment answer risk classification.
 *
 * Mirror of the backend classifier at
 * CF_Deforestation_Farmer_Service/src/assessment-builder/utils/assessment-risk.util.ts.
 * Keep the two in step: if the panel and the API disagree about what counts as
 * risk, a farm shows one status and is judged by another.
 *
 * The classification lives on assessment_question_options.value, which the
 * assessment builder writes as very_high | high | medium | low | zero. Any other
 * value (e.g. the seeded "Standard") means the option is not risk-classified.
 */

export const AssessmentAnswerRisk = {
  VERY_HIGH: 'very_high',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  VERY_LOW: 'very_low',
  ZERO: 'zero',
  UNCLASSIFIED: 'unclassified',
}

export const AssessmentRiskStatus = {
  APPROVED: 'approved',
  MITIGATION_REQUIRED: 'mitigation_required',
  REJECTED: 'rejected',
  MANUALLY_MITIGATED: 'manually_mitigated',
  NOT_APPLICABLE: 'not_applicable',
  // Validity window lapsed - the questionnaire must be answered again, which is
  // why this is kept distinct from MITIGATION_REQUIRED.
  EXPIRED: 'expired',
}

/** Human labels for the risk tiers, as shown on the answer badge. */
const RISK_LABELS = {
  [AssessmentAnswerRisk.VERY_HIGH]: 'Very High',
  [AssessmentAnswerRisk.HIGH]: 'High',
  [AssessmentAnswerRisk.MEDIUM]: 'Medium',
  [AssessmentAnswerRisk.LOW]: 'Low',
  [AssessmentAnswerRisk.VERY_LOW]: 'Very Low',
  [AssessmentAnswerRisk.ZERO]: 'Zero',
}

const RISK_COLOR_CLASSES = {
  [AssessmentAnswerRisk.VERY_HIGH]: 'very-high-color',
  [AssessmentAnswerRisk.HIGH]: 'high-color',
  [AssessmentAnswerRisk.MEDIUM]: 'medium-color',
  [AssessmentAnswerRisk.LOW]: 'low-color',
  [AssessmentAnswerRisk.VERY_LOW]: 'low-color',
  [AssessmentAnswerRisk.ZERO]: 'zero-color',
}

const EXACT_VALUE_RISK = {
  very_high: AssessmentAnswerRisk.VERY_HIGH,
  veryhigh: AssessmentAnswerRisk.VERY_HIGH,
  high: AssessmentAnswerRisk.HIGH,
  medium: AssessmentAnswerRisk.MEDIUM,
  med: AssessmentAnswerRisk.MEDIUM,
  very_low: AssessmentAnswerRisk.VERY_LOW,
  verylow: AssessmentAnswerRisk.VERY_LOW,
  low: AssessmentAnswerRisk.LOW,
  zero: AssessmentAnswerRisk.ZERO,
  negligible: AssessmentAnswerRisk.ZERO,
  zero_negligible: AssessmentAnswerRisk.ZERO,
  no_risk: AssessmentAnswerRisk.ZERO,
  none: AssessmentAnswerRisk.ZERO,
}

/**
 * Prefix fallbacks. Order matters: very_high and very_low must be tested before
 * high and low, otherwise "very_high" degrades to HIGH.
 */
const PREFIX_VALUE_RISK = [
  ['very_high', AssessmentAnswerRisk.VERY_HIGH],
  ['very_low', AssessmentAnswerRisk.VERY_LOW],
  ['zero_negligible', AssessmentAnswerRisk.ZERO],
  ['zero', AssessmentAnswerRisk.ZERO],
  ['negligible', AssessmentAnswerRisk.ZERO],
  ['high', AssessmentAnswerRisk.HIGH],
  ['medium', AssessmentAnswerRisk.MEDIUM],
  ['low', AssessmentAnswerRisk.LOW],
]

/** Lowercase, trim, and collapse separators seen in real data to underscore. */
function normalizeValue (value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s\-/\\]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

/** Classify a single assessment_question_options.value. */
export function classifyOptionValue (value) {
  const normalized = normalizeValue(value)
  if (!normalized) return AssessmentAnswerRisk.UNCLASSIFIED

  const exact = EXACT_VALUE_RISK[normalized]
  if (exact) return exact

  for (const [prefix, risk] of PREFIX_VALUE_RISK) {
    if (normalized === prefix || normalized.startsWith(`${prefix}_`)) return risk
  }

  return AssessmentAnswerRisk.UNCLASSIFIED
}

export function isClassified (risk) {
  return risk !== AssessmentAnswerRisk.UNCLASSIFIED
}

/**
 * True when this answer means "risk detected": classified, not zero, and the org
 * has not switched the tier off. A null/undefined toggle means enabled, so the
 * default is "anything that is not zero/negligible is risk".
 */
export function isRiskDetected (risk, riskLevels) {
  if (risk === AssessmentAnswerRisk.UNCLASSIFIED) return false
  if (risk === AssessmentAnswerRisk.ZERO) return false

  const toggle = riskLevels ? riskLevels[risk] : undefined
  return toggle === null || toggle === undefined ? true : Boolean(toggle)
}

/** Display label for a risk tier, or '' when the option is not classified. */
export function riskLabel (risk) {
  return RISK_LABELS[risk] || ''
}

/** CSS class for a risk tier, or '' when the option is not classified. */
export function riskColorClass (risk) {
  return RISK_COLOR_CLASSES[risk] || ''
}

/**
 * Resolve the risk of the option a response selected.
 *
 * `question.options` is the live option list; the value frozen on the answer is
 * the fallback, because editing a question deletes and recreates its options.
 */
export function classifyAnswer (question, response) {
  if (!question || !response) return AssessmentAnswerRisk.UNCLASSIFIED

  const selectedOptions = response.selectedOptions || []
  if (!selectedOptions.length) return AssessmentAnswerRisk.UNCLASSIFIED

  const risks = selectedOptions.map((selected) => {
    const option = (question.options || []).find(
      (o) => Number(o.id) === Number(selected.selectedOptionId),
    )
    return classifyOptionValue(
      option && option.value !== undefined && option.value !== null
        ? option.value
        : selected.optionValue,
    )
  })

  return worstRisk(risks)
}

const RISK_SEVERITY = {
  [AssessmentAnswerRisk.VERY_HIGH]: 6,
  [AssessmentAnswerRisk.HIGH]: 5,
  [AssessmentAnswerRisk.MEDIUM]: 4,
  [AssessmentAnswerRisk.LOW]: 3,
  [AssessmentAnswerRisk.VERY_LOW]: 2,
  [AssessmentAnswerRisk.ZERO]: 1,
  [AssessmentAnswerRisk.UNCLASSIFIED]: 0,
}

/** Worst tier wins, so a multi-select answer takes its riskiest option. */
export function worstRisk (risks) {
  return (risks || []).reduce(
    (worst, current) =>
      (RISK_SEVERITY[current] || 0) > (RISK_SEVERITY[worst] || 0) ? current : worst,
    AssessmentAnswerRisk.UNCLASSIFIED,
  )
}

/**
 * The single predicate for "does this status clear the compliance bar".
 *
 * manually_mitigated only counts while the mitigation is still valid.
 */
export function isAssessmentStatusCompliant (status, expiresOn, now = new Date()) {
  switch (normalizeValue(status)) {
    // No risk-classified questions means nothing to revoke.
    case AssessmentRiskStatus.NOT_APPLICABLE:
      return true

    case AssessmentRiskStatus.APPROVED:
    case AssessmentRiskStatus.MANUALLY_MITIGATED: {
      if (!expiresOn) return true
      const expiry = new Date(expiresOn)
      if (isNaN(expiry.getTime())) return true
      return expiry.getTime() > now.getTime()
    }

    default:
      return false
  }
}

/** Label + colour for a farm-level risk assessment status badge. */
export function assessmentStatusBadge (status, expiresOn) {
  const normalized = normalizeValue(status)

  switch (normalized) {
    case AssessmentRiskStatus.APPROVED:
      return isAssessmentStatusCompliant(status, expiresOn)
        ? { label: 'Compliant', color: 'success' }
        : { label: 'Assessment Expired', color: 'error' }
    case AssessmentRiskStatus.NOT_APPLICABLE:
      return { label: 'Not Applicable', color: 'grey' }
    case AssessmentRiskStatus.MANUALLY_MITIGATED:
      return isAssessmentStatusCompliant(status, expiresOn)
        ? { label: 'Manually Mitigated', color: 'info' }
        : { label: 'Mitigation Expired', color: 'error' }
    case AssessmentRiskStatus.MITIGATION_REQUIRED:
      return { label: 'Mitigation Required', color: 'warning' }
    case AssessmentRiskStatus.EXPIRED:
      return { label: 'Assessment Expired', color: 'error' }
    case AssessmentRiskStatus.REJECTED:
      return { label: 'Rejected', color: 'error' }
    default:
      return { label: 'Pending', color: 'grey' }
  }
}

/** Status filter options, including the two statuses added for auto-classification. */
export const RISK_ASSESSMENT_STATUS_FILTER_OPTIONS = [
  { text: 'Compliant', value: AssessmentRiskStatus.APPROVED },
  { text: 'Mitigation Required', value: AssessmentRiskStatus.MITIGATION_REQUIRED },
  { text: 'Manually Mitigated', value: AssessmentRiskStatus.MANUALLY_MITIGATED },
  { text: 'Not Applicable', value: AssessmentRiskStatus.NOT_APPLICABLE },
  { text: 'Assessment Expired', value: AssessmentRiskStatus.EXPIRED },
  { text: 'Rejected', value: AssessmentRiskStatus.REJECTED },
]
